import type { z } from 'zod'

import { getEffectiveApiBaseUrl } from '@/lib/env'

const STORAGE_KEY = 'vectraclip-jwt'

// -----------------------------------------------------------------------------
// Erro tipado para qualquer falha na borda da rede
// -----------------------------------------------------------------------------

export class ApiError extends Error {
  readonly status: number
  readonly path: string
  readonly body: string | undefined

  constructor(status: number, message: string, path: string, body?: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.path = path
    this.body = body
  }
}

export class ApiValidationError extends Error {
  readonly path: string
  readonly issues: z.ZodIssue[]

  constructor(path: string, issues: z.ZodIssue[]) {
    super(`Resposta inválida de ${path}: ${formatIssues(issues)}`)
    this.name = 'ApiValidationError'
    this.path = path
    this.issues = issues
  }
}

/** Resumo legível das primeiras issues — vira mensagem do toast/console. */
function formatIssues(issues: z.ZodIssue[]): string {
  if (issues.length === 0) return 'sem detalhes'
  const lines = issues.slice(0, 3).map((issue) => {
    const path = issue.path.length > 0 ? issue.path.join('.') : '<root>'
    const extra =
      'received' in issue && issue.received !== undefined
        ? ` (recebido: ${JSON.stringify(issue.received)})`
        : ''
    return `${path} → ${issue.message}${extra}`
  })
  if (issues.length > 3) {
    lines.push(`(+${issues.length - 3} outras)`)
  }
  return lines.join(' | ')
}

// -----------------------------------------------------------------------------
// Cliente
// -----------------------------------------------------------------------------

type GetToken = () => string | null

/**
 * Tenta renovar a sessão. Retorna o novo access token quando bem-sucedido,
 * ou `null` quando o refresh falhar (cliente vai disparar `onUnauthorized`).
 *
 * Implementação tipicamente vive no `AuthProvider` (sabe o refresh token,
 * sabe persistir a nova sessão, sabe atualizar o React state).
 */
type RefreshSession = () => Promise<string | null>

interface ApiClientOptions {
  baseUrl?: string
  getToken?: GetToken
}

/**
 * Hooks de auth — opt-in, plugados em runtime pelo `AuthProvider` chamando
 * `apiClient.installAuthHooks(...)`. Por que não passar via construtor:
 * o `apiClient` é singleton importado direto pelos endpoints, e o
 * AuthProvider só monta DEPOIS — então a instalação é tardia.
 */
export interface AuthHooks {
  /**
   * Chamado quando uma request retorna 401. Deve tentar renovar a sessão e
   * retornar o novo access token. Se retornar `null`, o cliente trata como
   * "refresh falhou" e dispara `onUnauthorized`.
   */
  refresh: RefreshSession
  /**
   * Chamado quando o refresh também falha (sessão definitivamente morta).
   * Tipicamente: limpa storage e redireciona para `/login`.
   */
  onUnauthorized: () => void
}

export class ApiClient {
  private readonly getToken: GetToken
  private authHooks: AuthHooks | null = null
  /**
   * Single-flight do refresh: várias requests 401 simultâneas dividem a
   * mesma chamada `/auth/refresh`. Sem isso, num burst de 5 GETs paralelos
   * que expiram juntos, faríamos 5 refresh requests e rotações concorrentes
   * invalidariam umas às outras.
   */
  private inflightRefresh: Promise<string | null> | null = null

  constructor(opts: ApiClientOptions = {}) {
    this.getToken = opts.getToken ?? defaultGetToken
    if (opts.baseUrl !== undefined) {
      this._baseUrlOverride = opts.baseUrl
    }
  }

  /** Fixo só em testes (`new ApiClient({ baseUrl })`). Produção relê env a cada request. */
  private _baseUrlOverride?: string

  private resolveBaseUrl(): string {
    if (this._baseUrlOverride !== undefined) return this._baseUrlOverride
    return getEffectiveApiBaseUrl()
  }

  /** Plugado pelo AuthProvider no boot. Idempotente — sobrescreve. */
  installAuthHooks(hooks: AuthHooks): void {
    this.authHooks = hooks
  }

  /** Usado em testes pra resetar o estado entre cenários. */
  uninstallAuthHooks(): void {
    this.authHooks = null
    this.inflightRefresh = null
  }

  /**
   * Request cru, sem validação de schema. Use para endpoints que não retornam
   * payload tipado (ex.: 204 No Content) ou quando você fará validação manual.
   */
  async request<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
    return this.requestWithRetry<T>(path, init, false)
  }

  get<T>(path: string, init?: RequestInit): Promise<T> {
    return this.request<T>(path, { ...init, method: 'GET' })
  }

  post<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
    return this.request<T>(path, {
      ...init,
      method: 'POST',
      body: body ? JSON.stringify(body) : null,
    })
  }

  put<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
    return this.request<T>(path, {
      ...init,
      method: 'PUT',
      body: body ? JSON.stringify(body) : null,
    })
  }

  delete<T>(path: string, init?: RequestInit): Promise<T> {
    return this.request<T>(path, { ...init, method: 'DELETE' })
  }

  /** Faz uma request e retorna o Response cru (sem consumir o body). Útil para SSE/streaming. */
  async stream(path: string, init: RequestInit = {}): Promise<Response> {
    const token = this.getToken()
    const headers = new Headers(init.headers)
    if (!headers.has('Content-Type') && init.body !== undefined) {
      headers.set('Content-Type', 'application/json')
    }
    if (token && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`)
    }
    return fetch(`${this.resolveBaseUrl()}${path}`, { ...init, headers })
  }

  /**
   * Request com validação Zod obrigatória. Falha com `ApiValidationError`
   * quando o payload da rede não bate com o schema (drift backend ↔ frontend).
   */
  async requestValidated<S extends z.ZodTypeAny>(
    path: string,
    schema: S,
    init: RequestInit = {},
  ): Promise<z.infer<S>> {
    const raw = await this.request<unknown>(path, init)
    const parsed = schema.safeParse(raw)
    if (!parsed.success) {
      throw new ApiValidationError(path, parsed.error.issues)
    }
    return parsed.data
  }

  // ---------------------------------------------------------------------------
  // Internos
  // ---------------------------------------------------------------------------

  private async requestWithRetry<T>(
    path: string,
    init: RequestInit,
    isRetry: boolean,
  ): Promise<T> {
    const token = this.getToken()
    const headers = new Headers(init.headers)
    if (
      !headers.has('Content-Type') &&
      init.body !== undefined &&
      !(init.body instanceof FormData)
    ) {
      headers.set('Content-Type', 'application/json')
    }
    if (token && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`)
    }

    const url = `${this.resolveBaseUrl()}${path}`
    const res = await fetch(url, { ...init, headers })

    if (res.ok) {
      if (res.status === 204) return undefined as T
      const text = await res.text()
      if (text.length === 0) return undefined as T
      return JSON.parse(text) as T
    }

    // ---- 401: tenta refresh + retry uma única vez --------------------------
    //
    // Regras:
    // - Só interceptamos 401 que NÃO seja em endpoints de auth (login/refresh
    //   precisam falhar visivelmente; refresh chama `requestValidated` ele
    //   mesmo, então um 401 lá significa "refresh inválido", e tentar refresh
    //   de novo seria loop infinito).
    // - `isRetry` impede mais de uma tentativa por request. Se o token recém-
    //   renovado também der 401, é problema do servidor — propagamos o erro.
    // - Sem `authHooks`, não há nada que possamos fazer: propagamos o 401
    //   normalmente (cenário típico: testes que não plugaram o hook).
    if (
      res.status === 401 &&
      !isRetry &&
      this.authHooks &&
      !isAuthEndpoint(path)
    ) {
      const newToken = await this.runRefreshOnce()
      if (newToken) {
        // Retry com o novo token. `getToken()` agora retorna o token novo
        // (AuthProvider já persistiu antes de resolver o promise).
        return this.requestWithRetry<T>(path, init, true)
      }
      // Refresh falhou → notifica AuthProvider para limpar storage e
      // redirecionar. Ainda assim, propagamos o ApiError original — quem
      // chamou pode mostrar uma toast / loga / etc.
      this.authHooks.onUnauthorized()
    }

    const body = await safeText(res)
    throw new ApiError(
      res.status,
      `${res.status} ${res.statusText} em ${path}`,
      path,
      body,
    )
  }

  private runRefreshOnce(): Promise<string | null> {
    if (!this.authHooks) return Promise.resolve(null)
    if (this.inflightRefresh) return this.inflightRefresh
    const promise = this.authHooks.refresh().finally(() => {
      this.inflightRefresh = null
    })
    this.inflightRefresh = promise
    return promise
  }
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function defaultGetToken(): string | null {
  if (typeof window === 'undefined') return null
  // Storage v2 (VEC-140): valor é JSON serializado de AuthSession. Compat
  // com o formato v1 (string crua) para zero-fricção em sessões antigas:
  // se não for JSON parseable, devolvemos a string como token.
  const raw = window.localStorage.getItem(STORAGE_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as unknown
    if (
      parsed &&
      typeof parsed === 'object' &&
      'accessToken' in parsed &&
      typeof (parsed as { accessToken: unknown }).accessToken === 'string'
    ) {
      return (parsed as { accessToken: string }).accessToken
    }
    return raw
  } catch {
    return raw
  }
}

async function safeText(res: Response): Promise<string | undefined> {
  try {
    return await res.text()
  } catch {
    return undefined
  }
}

function isAuthEndpoint(path: string): boolean {
  // Endpoints de auth precisam falhar visivelmente em 401:
  // - /auth/login: 401 = credencial errada → mostra inline na UI
  // - /auth/refresh: 401 = sessão morta → AuthProvider faz logout
  // - /auth/logout: irrelevante (best-effort)
  return path.startsWith('/auth/') || path.startsWith('/api/auth/')
}

// -----------------------------------------------------------------------------
// Singleton padrão (lê JWT de localStorage)
// -----------------------------------------------------------------------------

export const apiClient = new ApiClient()
export const api = apiClient
